import type { Schemas } from '@argzombie/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorText, ok, useApi } from '../lib/api';

/**
 * The player detail's "Test account" switch (admin tools): flips `players.is_test_account` through
 * `PUT /admin/players/{id}/test-account` after asking for a reason (the API needs Admin and audits it). Test accounts are
 * hidden from the player list and left out of the dashboard, and the dev purge deletes them.
 */
export function TestAccountSwitch({ player }: { player: Schemas['AdminPlayerRow'] }) {
  const { client } = useApi();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const flip = useMutation({
    mutationFn: async (body: { isTest: boolean; reason: string }) =>
      ok(
        await client.PUT('/admin/players/{id}/test-account', {
          params: { path: { id: player.id } },
          body,
        }),
      ),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin-player', player.id] });
      void qc.invalidateQueries({ queryKey: ['admin-players'] });
    },
    onError: (e) => setError(errorText(e)),
  });

  const toggle = () => {
    const isTest = !player.isTestAccount;
    const reason = window.prompt(
      isTest ? 'Why is this a test account?' : 'Why is this a real player?',
      '',
    );
    if (!reason?.trim()) return;
    flip.mutate({ isTest, reason: reason.trim() });
  };

  return (
    <p className="mb-4 flex flex-wrap items-center gap-2 text-sm">
      <label className="flex items-center gap-2 font-semibold">
        <input
          type="checkbox"
          role="switch"
          aria-label="Test account"
          checked={player.isTestAccount ?? false}
          disabled={flip.isPending}
          onChange={toggle}
        />
        Test account
      </label>
      {player.isTestAccount && player.testAccountReason && (
        <span className="text-bruise">({player.testAccountReason})</span>
      )}
      {error && <span className="text-blam">{error}</span>}
    </p>
  );
}
